import React, { ReactElement, RefObject } from 'react';
import { RadioGroup, RadioGroupChangeEvent, TextAreaChangeEvent } from "@progress/kendo-react-inputs";
import {
  Component,
  Container,
  HBox, Position,
  PuzzleButton,
  PuzzleTextArea,
  PuzzleTooltip as TpTooltip,
  VBox,
  WithTooltip
} from "puzzle";
import KendoGridAbstract from "../../util/KendoGridAbstract/KendoGridAbstract";
import { GridColumn as Column } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import { GridCellProps, GridRowClickEvent, GridRowProps } from "@progress/kendo-react-grid";
import Big from "big.js";
import * as _ from "lodash";
import NegotiationGrid from "./NegotiationGrid/NegotiationGrid";
import LocalizedDatePicker from "../../util/LocalizedDatePicker/LocalizedDatePicker";
import { DatePickerChangeEvent } from "@progress/kendo-react-dateinputs";
import { getUnixDate } from "../../util/Util";
import { FINANCIAL_DOCUMENT, OFFER_TASK, POST_BUDGET_RATE, PREVIEW_ITEM } from "./Types";
import { NEGOTIATION_ACTION_NAME } from "./NegotiationGrid/Types";
import LimitedLetterCounter from "../../Submission/sections/Details/TemplateDescription/LimitedLetterCounter";
import ActionExecutorInstance from "../TaskList/ActionExecutor/ActionExecutor";

type PROPS = {
  responseData: any,
  submissionData: any
};

type STATE = {
  actionName?: NEGOTIATION_ACTION_NAME,
  data: OFFER_TASK[],
  selectedTask: OFFER_TASK
};

const readOnlyStatuses: string[] = [
  "APPROVED",
  "FINALIZED",
  "INVOICING_AVAILABLE",
  "PO_REQUESTED",
  "PO_SENT"
];
const previewEditors: string[] = ["TSO", "TSR", "GLT", "GLR"];

class OfferDialog extends Component<PROPS, STATE> {
  private allReadOnlyTasks: boolean = false;
  private tooltip: RefObject<HTMLElement> = React.createRef();
  private changesExists: boolean = false;
  public state: STATE = {
    data: [],
    actionName: '',
    selectedTask: {
      allowNegotiation: true,
      availableEditors: [],
      background: '',
      backgroundEnabled: false,
      batchLanguageFileCount: 0,
      batchLanguageWordCount: 0,
      batchName: '',
      budgetRates: [],
      currency: '',
      currentScopeDueDate: 0,
      cva: false,
      dueDate: 0,
      excludedScopes: [],//multi role tasks
      financialDetails: [],
      parsableState: '',
      previewEnabled: true,
      referenceFilesCount: 0,
      instructions: '',
      instructionsEnabled: false,
      isBudgetTask: true,
      linkedScopes: [],//combined role tasks
      offer: 0,
      phaseName: '',
      phaseType: '',
      readOnly: false,
      scopeId: '',
      status: "PROVIDED",
      targetLanguage: {
        targetLanguageId: '',
        targetLanguageDisplayName: ''
      },
      taskIds: [],
      unitOfWork: 'FLAT',
      total: 0
    }
  }

  moduleDidMount = () => {
    const data = this.getTaskListData();
    const selectedTask: OFFER_TASK = this.getInitSelectedTask(data);
    let stateData: any = {
      data, selectedTask
    }
    if (selectedTask.status === 'COUNTERED') {
      stateData.actionName = 'negotiate';
      this.changesExists = true;
    }
    if (selectedTask.status === 'ACCEPTED') {
      stateData.actionName = 'accept';
    }
    this.setState({ ...stateData });
    this.allReadOnlyTasks = data.every((task: OFFER_TASK) => readOnlyStatuses.includes(task.status));
    if (this.allReadOnlyTasks) {
      this.dispatch('updateReadOnlyOfferDialog', Position.end, {
        showClose: true,
        hideNext: true
      });
    } else {
      this.dispatch('updateFooterButtons', Position.end, {
        displayCVABadge: selectedTask.cva,
        enableNext: this.changesExists
      });
    }
  }

  /**
   * Returns task that should be initially preselected
   *
   * @param data
   */
  getInitSelectedTask = (data: OFFER_TASK[]): OFFER_TASK => {
    const { taskId } = this.props.submissionData;
    const task: any = _.find(data, (task: OFFER_TASK) => {
      if (task.taskIds.includes(taskId)) {
        return true;
      }
    }) || data[0];
    return task;
  };

  /**
   * Copies all props needed for preview on ClamOffer
   * @param item
   * @param isLinkedScope
   */
  addPreviewItem = (item: OFFER_TASK, isLinkedScope = false) => {
    const {
      batchLanguageFileCount: fileCount,
      batchLanguageWordCount: wordCount,
      batchName,
      cva,
      dueDate,
      supplierProposedDueDate,
      phaseName,
      targetLanguage,
      offer,
      userAction: offerStatus
    } = item;

    return {
      batchName,
      cva,
      dueDate: supplierProposedDueDate || dueDate,
      isLinkedScope,
      fileCount,
      offerStatus,
      phaseName,
      targetLanguageDisplayName: targetLanguage.targetLanguageDisplayName,
      total: offer,
      wordCount
    };
  };
  /**
   * Enables next button on any change (once only)
   */
  enableNextButton = () => {
    if (!this.changesExists) {
      this.changesExists = true;
    }
    const { selectedTask } = this.state;
    if (!this.allReadOnlyTasks) {
      this.dispatch('updateFooterButtons', '', {
        displayCVABadge: selectedTask.cva,
        enableNext: true
      });
    }
  };

  /**
   * Grid cell render
   *
   * @param defaultRendering
   * @param cellProps
   */
  cellRender = (defaultRendering: React.ReactElement<HTMLTableCellElement> | null, cellProps: GridCellProps): JSX.Element => {
    const { dataItem, field } = cellProps;
    if (defaultRendering && field) {
      let children;
      let editedProps = {
        ...defaultRendering.props
      };

      const value = dataItem[field];
      switch (cellProps.field) {
        case 'phaseName':
          children = value;
          if (dataItem.linkedScopes?.length) {
            children =
              <div className={'tp-pointer-events-none'}>
                <HBox alignItemsCenter>
                  <span className={'tp-text-ellipsis tp-text-medium'}>{value}</span>
                </HBox>
                {dataItem.linkedScopes.map((scope: any, index: number) => {
                  return !scope.isBudgetTask ? <HBox alignItemsCenter className={'pd-additional-row tp-color-light'}
                                                     key={`phase${scope}${index}`}>
                    <i aria-hidden="true" className={'tp-icon tp-icon-sm tp-copy-to-clipboard'} />
                    <span className={'tp-text-ellipsis'}>{scope.phaseName}</span>
                  </HBox> : null
                })}
              </div>
          }
          break;
        case 'dueDate':
          children = value ? this.translateByNamespace('date.dateWithTime', { date: new Date(value) }) : null;
          if (dataItem.linkedScopes?.length) {
            children = (
              <div className={'tp-pointer-events-none'}>
                <span className={'tp-text-ellipsis pd-due-date-cell'}>{children}</span>
                {dataItem.linkedScopes.map((scope: any, index: number) => {
                  return !scope.isBudgetTask ? <HBox alignItemsCenter className={'pd-additional-row tp-color-light'}
                                                     key={`dueDate${scope}${index}`}>
                    <span>{scope.dueDate ? this.translateByNamespace('date.dateWithTime', { date: new Date(scope.dueDate) }) : null}</span>
                  </HBox> : null;
                })}
              </div>
            );
          }
          break;
        case 'batchName':
          children =
            <div className={'tp-pointer-events-none'}>
              <HBox alignItemsCenter>
                <span className={'tp-text-ellipsis'}>{value}</span>
              </HBox>
              {!!dataItem.linkedScopes?.length && dataItem.linkedScopes.map((scope: any, index: number) => {
                return !scope.isBudgetTask ? <HBox alignItemsCenter className={'pd-additional-row tp-color-light'}
                                                   key={`batch${scope}${index}`}>
                  <span className={'tp-text-ellipsis'}>{scope.batchName}</span>
                </HBox> : null;
              })}
            </div>
          break;
        case 'offer':
          editedProps.className += ' k-text-right tp-text-semibold pd-offer-cell';
          children = this.offerRenderer(dataItem, value);
          break;
        default:
          children = value;
          break;
      }

      return React.cloneElement(defaultRendering, editedProps, children);
    }
    return <></>
  };

  /**
   * Creates budgets post data
   */
  createPostData = () => {
    let budgets = this.state.data.reduce((array: any, item: OFFER_TASK) => {
      let budgetItem: any;
      const {
        userAction
      } = item;

      if (userAction) {
        const {
          batchName,
          comment,
          linkedScopes,
          phaseName,
          supplierProposedDueDate,
          status,
          targetLanguage
        } = item;

        const hasCancelledItem = ['accept', 'negotiate', 'decline'].includes(userAction) && ["COUNTERED", "ACCEPTED"].includes(status);
        if (hasCancelledItem) { //If the vendor changes its mind and "re-counter" his own offer, the previous offer(counter offer or accept w/ cva) should be cancelled.
          const canceledItem = {
            financialDocumentScope: {
              batchName,
              languageId: targetLanguage.targetLanguageId,
              phaseName
            },
            vendorBudgetAction: 'CANCEL',
          }
          array.push(canceledItem);
        }
        budgetItem = {
          ...this.getBudgetRates(item),
          ...(comment && { comment }),
          ...(['accept', 'decline'].includes(userAction) && linkedScopes?.length > 0 && {
              linkedScopes: linkedScopes.reduce((array: string[], linkedScope: any) => {
                if (!linkedScope.isBudgetTask) {
                  array.push(linkedScope.scopeId);
                }
                return array;
              }, [])
            }
          ),
          financialDocumentScope: {
            batchName,
            languageId: targetLanguage.targetLanguageId,
            phaseName
          },
          vendorBudgetAction: _.toUpper(item.userAction),
          ...(supplierProposedDueDate && { supplierProposedDueDate })
        }
        array.push(budgetItem);
      }

      return array;
    }, []);
    return {
      budgets
    };
  }

  /**
   Return true if all task has been declined by supplier user
   */
  hasAllTaskDeclined = (): boolean => {
    const { data } = this.state;
    return data.every((currentData: OFFER_TASK) => currentData.userAction === 'decline');
  };

  /**
   * Return true if at least one change has been made
   */
  hasChanges = () => this.changesExists;

  /**
   * Decline all tasks action
   */
  declineAllTask = () => {
    let { data, selectedTask } = this.state;
    data = data.map((currentTask: OFFER_TASK) => {
      let task = currentTask;
      if (!readOnlyStatuses.includes(task.status)) {
        task = {
          ...currentTask,
          budgetRates: [],
          supplierProposedDueDate: 0,
          userAction: 'decline'
        }
      }
      return task;
    });
    selectedTask = _.find(data, (item: OFFER_TASK) => item.scopeId === selectedTask.scopeId) || data[0];
    this.setState({ data, selectedTask, actionName: 'decline' }, () => this.enableNextButton());
  };

  /**
   * Download non-parsable files
   */
  downloadSourceFiles = () => {
    const { taskIds } = this.state.selectedTask;
    this.dispatch('downloadFiles', '', {
      submissionData: { taskId: taskIds[0] },
      params: { downloadType: 'sources' }
    });
  };

  /**
   * Download reference files
   */
  downloadReferenceFiles = () => {
    const paramsReferenceFiles = { downloadType: 'references' };
    const { selectedTask } = this.state;
    const submissionData = {
      submissionName: this.props.responseData.submissionName,
      taskId: selectedTask.taskIds[0]
    }
    this.dispatch('downloadFiles', '', { submissionData, params: paramsReferenceFiles });
  };

  /**
   * Returns adjusted linked scopes
   *
   * @param linkedScopes
   */
  getAdjustedLinkedScopes = (linkedScopes: string[]) => {
    const tasks = this.props.responseData.tasks;
    let item = linkedScopes.length;
    let linkedData = [];
    while (item--) {
      let currentScopeId = linkedScopes[item];
      const matchingTask = tasks.find((currentTask: any) => currentTask.scope?.scopeId === currentScopeId);
      if (matchingTask) {
        linkedData.push({
          ...matchingTask.scope,
          isBudgetTask: matchingTask.isBudgetTask,
          dueDate: matchingTask.currentScopeDueDate
        })
      }
    }
    return linkedData;
  };

  /**
   * Creates budget rate for given task
   */
  getBudgetRates = (item: OFFER_TASK) => {
    const {
      budgetRates,
      financialDetails
    } = item;
    let returnedBudgetRates;

    if (item.userAction === "negotiate") {
      if (!budgetRates) {
        returnedBudgetRates = financialDetails[financialDetails.length - 1]?.rates.map((rate) => {
          return {
            budgetRateName: rate.budgetRateNameEnum.name,
            rate: rate.pricePerUnit
          }
        });
      } else {
        returnedBudgetRates = budgetRates.map(this.mapRates.bind(item))
      }
    } else {
      returnedBudgetRates = budgetRates?.length > 0 ? budgetRates.map(this.mapRates.bind(item)) : null;
    }

    return returnedBudgetRates === null ? null : { budgetRates: returnedBudgetRates };
  };

  /**
   * Returns all post data including submission id
   */
  getPostData = () => {
    return {
      submissionId: this.props.submissionData.submissionId,
      postData: this.createPostData()
    }
  };

  /**
   * Returns task list data
   */
  getTaskListData = () => {
    return this.props.responseData.tasks.reduce((taskArray: OFFER_TASK[], task: any) => {
      if (task.isBudgetTask) {
        if (task.status === 'COUNTERED') {
          task.userAction = 'negotiate'; //To preselect radio button on open
          task.supplierProposedDueDate = task.currentScopeDueDate;
          task.comment = task.financialDetails[1]?.comment;
        }
        if (task.status === 'UPDATED') {
          task.cva = false; //If client responded cva is irrelevant
        }
        taskArray.push({
          ...task.scope,
          ...task,
          linkedScopes: this.getAdjustedLinkedScopes(task.linkedScopes),
          dueDate: task.currentScopeDueDate,
          readOnly: readOnlyStatuses.includes(task.status),
          offer: task.total
        });
      }
      return taskArray;
    }, []);
  }

  /**
   * Negotiate due date
   *
   * @param event
   * @param minData
   */
  onDateChange = (event: DatePickerChangeEvent, minData: number) => {
    const value = event.target.value;
    if (value) {
      const unixDate = getUnixDate(value);
      let { data, selectedTask } = this.state;
      const { scopeId } = selectedTask;
      data = data.map((currentTask: any) => {
        if (currentTask.scopeId === scopeId) {
          currentTask.supplierProposedDueDate = currentTask.dueDate = unixDate;
          currentTask.userAction = 'negotiate'; //set to negotiate in case if there was "UPDATED" server status before
        }
        return currentTask;
      });
      this.setState({ data }, () => {
        this.validateNewDate(minData)
      });
    }
  };

  /**
   * Sets invalidDate to true if chosen date is less then min date
   *
   * @param minData
   */
  validateNewDate = (minData: number) => {
    const { selectedTask } = this.state;
    const { dueDate } = this.state.selectedTask;
    let invalidDate = dueDate < minData;
    this.dispatch('updateFooterButtons', '', {
      displayCVABadge: selectedTask.cva,
      enableNext: !invalidDate
    });
  };

  /**
   * Prepare data for post and preview on claimOfferDialog
   */
  getClaimOfferDialogData = () => {
    const {
      claimLevel,
      currency,
      roundingDigits
    } = this.props.responseData;

    const postData = this.createPostData();
    const previewData = this.state.data.reduce((changedTasks: PREVIEW_ITEM[], item: OFFER_TASK) => {
      if (item.userAction) {
        changedTasks.push(this.addPreviewItem(item));
        item.linkedScopes?.forEach((linkedItem: any) => {
          if (!linkedItem.isBudgetTask) {
            changedTasks.push(this.addPreviewItem({
              ...linkedItem,
              batchLanguageFileCount: item.batchLanguageFileCount,
              batchLanguageWordCount: item.batchLanguageWordCount,
            }, true))
          }
        });
      }
      return changedTasks;
    }, []);

    return {
      claimLvl: _.toUpper(claimLevel),
      currency,
      postData,
      previewData,
      roundingDigits,
      submissionData: this.props.submissionData
    }
  };

  /**
   * Map budget rates
   * @param item
   */
  mapRates = (item: POST_BUDGET_RATE) => {
    return {
      ...item,
      budgetRateName: _.toUpper(item.budgetRateName)
    }
  };

  /**
   * Offer renderer
   *
   * @param dataItem
   * @param value
   */
  offerRenderer = (dataItem: OFFER_TASK, value: number) => {
    let className: string = 'tp-check-underline-circle';
    const roundingDigits: number = this.props.responseData.roundingDigits;
    let tooltipText = '';
    if (dataItem.userAction) {
      switch (dataItem.userAction) {
        case 'accept':
          tooltipText = this.translate('tooltipOffer.APPROVED');
          className = 'tp-check-circle';
          if (dataItem.cva) {
            tooltipText = this.translate('tooltipOffer.ACCEPTED');
            className = 'tp-arrow-left-circle-filled'
          }
          break;
        case 'decline':
          className = 'tp-close-circle';
          tooltipText = this.translate('tooltipOffer.DECLINED');
          break;
        case 'negotiate':
          className = 'tp-arrow-left-circle-filled';
          tooltipText = this.translate('tooltipOffer.COUNTERED');
          break;
        default:
          break;
      }
    } else {
      let { status } = dataItem;
      tooltipText = this.translate(`tooltipOffer.${status}`);
      if (status === 'PO_REQUESTED') {
        status = 'APPROVED';
      }
      switch (status) {
        case 'PROVIDED':
          className = 'tp-check-underline-circle';
          break;
        case 'APPROVED':
          className = 'tp-check-circle';
          break;
        case 'ACCEPTED':
        case 'COUNTERED':
          className = 'tp-arrow-left-circle-filled';
          break;
        case 'UPDATED':
          className = 'tp-arrow-up-right-circle';
          break;
        case 'PO_SENT':
          className = 'po-circle-filled';
          break;
        case 'FINALIZED':
          className = 'stars';
          break;
        case 'INVOICING_AVAILABLE':
          className = 'dollar-circle-filled';
          break;
        default:
          break;
      }
    }
    return (
      <div className={'tp-pointer-events-none'}>
        <HBox alignItemsCenter justifyContentEnd>
          <span>{value.toFixed(roundingDigits)}</span>
          <WithTooltip
            className={'k-d-flex'}
            context={this}
            htmlTag='span'
            title={tooltipText}
          >
            <span className={`tp-icon ${className} k-pointer-events-auto k-cursor-pointer`} />
          </WithTooltip>
        </HBox>
        {!!dataItem.linkedScopes?.length &&
          dataItem.linkedScopes.map((scope: any, index: number) => {
            return !scope.isBudgetTask ?
              <HBox alignItemsCenter justifyContentEnd key={`offer${scope.scopeId}${index}`}> -
              </HBox> : null
          })
        }
      </div>
    );
  };

  /**
   * Text area change event. Save value to state
   *
   * @param event
   */
  onTextAreaChange = (event: TextAreaChangeEvent) => {
    let { value: comment } = event;
    const { selectedTask } = this.state;
    selectedTask.comment = comment;
    selectedTask.userAction = 'negotiate';
    this.setState({
      selectedTask
    });
  };

  /**
   * Opens background instructions dialog
   */
  openBackgroundInstructions = () => {
    const { background, instructions, phaseType, backgroundEnabled, instructionsEnabled } = this.state.selectedTask;
    this.dispatch('openBackgroundInstructionsDialog', '', {
      background,
      backgroundEnabled,
      instructionsEnabled,
      instructions,
      phaseType,
      submissionId: this.props.responseData.submissionId
    })
  };

  /**
   * Open files in preview mode
   */
  openFilesInPreviewMode = () => {
    const { availableEditors, taskIds } = this.state.selectedTask;
    const editors = _.intersection(availableEditors, previewEditors);
    if (editors.length) {
      ActionExecutorInstance.previewTask({ availableEditors: editors, taskId: taskIds[0] });
    }
  };

  /**
   * Open word count dialog
   */
  openWordCount = () => {
    const { responseData } = this.props;
    const { selectedTask } = this.state;
    const { submissionName, submissionId } = responseData;
    this.dispatch('openSupplierWordCount', '', {
      wordCountParams: {
        taskId: selectedTask.taskIds[0]
      },
      submissionData: {
        ...responseData,
        taskId: selectedTask.taskIds[0]
      },
      submissionName,
      submissionId
    })
  }

  /**
   * Handles radio action changes
   * Updates all corresponding statuses
   *
   * @param value
   */
  radioActionsOnChange = ({ value }: RadioGroupChangeEvent) => {
    const { claimLevel } = this.props.responseData;
    let { data, selectedTask } = this.state;
    const { scopeId } = selectedTask;
    let linkedTasks = selectedTask.linkedScopes.map((scope: any) => scope.scopeId);
    data = data.map((currentTask: OFFER_TASK) => {
      switch (value) {
        case 'accept':
          if (currentTask.scopeId === scopeId) {
            currentTask.userAction = value;
            currentTask.supplierProposedDueDate = 0;
            this.updateCurrentTaskOnRadioChange(currentTask);
          } else {
            if (claimLevel === 'LANGUAGE') {
              if (linkedTasks.includes(currentTask.scopeId)) {
                currentTask.userAction = 'accept';
                this.updateCurrentTaskOnRadioChange(currentTask);
              }
            }
            if (selectedTask.excludedScopes.includes(currentTask.scopeId)) {
              currentTask.userAction = 'decline';
              this.updateCurrentTaskOnRadioChange(currentTask);
            }
          }
          break;
        case 'decline':
          if (currentTask.scopeId === scopeId) {
            currentTask.userAction = value;
            currentTask.supplierProposedDueDate = 0;
            this.updateCurrentTaskOnRadioChange(currentTask);
          } else {
            if (claimLevel === 'LANGUAGE' && !readOnlyStatuses.includes(currentTask.status)) {
              if (linkedTasks.includes(currentTask.scopeId)) {
                currentTask.userAction = 'decline';
                this.updateCurrentTaskOnRadioChange(currentTask);
              }
            }
          }
          break;
        case 'negotiate':
          if (currentTask.scopeId === scopeId) {
            currentTask.userAction = value;
            currentTask.supplierProposedDueDate = currentTask.currentScopeDueDate;
          } else {
            if (claimLevel === 'LANGUAGE' && !readOnlyStatuses.includes(currentTask.status)) {
              if ((linkedTasks.includes(currentTask.scopeId)) && currentTask.allowNegotiation) {
                currentTask.userAction = 'negotiate';
                currentTask.supplierProposedDueDate = currentTask.currentScopeDueDate;
              }
            }
            if (selectedTask.excludedScopes.includes(currentTask.scopeId)) {
              currentTask.userAction = 'decline';
              this.updateCurrentTaskOnRadioChange(currentTask);
            }
          }
          break;
        default:
          break;
      }
      return currentTask;
    });
    this.setState({ actionName: value, data }, () => {
      this.enableNextButton();
    });
  };

  /**
   * Updates current task w/ corresponding data from financial document
   *
   * @param task
   */
  updateCurrentTaskOnRadioChange = (task: OFFER_TASK) => {
    const { financialDetails } = task;
    task.budgetRates = [];
    task.comment = '';
    switch (task.status) {
      case 'COUNTERED':
      case 'PROVIDED':
        task.offer = financialDetails[0].total;
        task.dueDate = financialDetails[0].dueDate;
        break;
      case 'UPDATED':
        task.offer = financialDetails[2].total;
        task.dueDate = financialDetails[2].dueDate;
        break;
      default:
        break;
    }
  };

  /**
   * Render for comment section
   */
  renderCommentSection = () => {
    const { userAction, status, comment, financialDetails = [] } = this.state.selectedTask;
    const commentEditorAvailable = userAction === 'negotiate';
    const renderCommentHeader = commentEditorAvailable || _.find(financialDetails, item => (item.status === 'COUNTERED' || item.status === 'UPDATED') && item.comment);
    const commentValue = comment || ""; //Can be null
    return (
      <>
        {renderCommentHeader && <h3 className={'tp-text-semibold tp-font-size-md'}>{this.translate('comment')}</h3>}
        {commentEditorAvailable && <>
          <PuzzleTextArea
            maxLength={1000}
            onChange={this.onTextAreaChange}
            placeholder={this.translate('addComment')}
            value={commentValue}
          />
          <LimitedLetterCounter
            className={'tp-color-light'}
            stringToCount={commentValue}
            maxCharacters={1000}
          />
        </>}
        {(financialDetails.length > 1 && status !== 'COUNTERED') && financialDetails.slice(1).map((currentDocument: FINANCIAL_DOCUMENT, index: number) => {
          return (['COUNTERED', 'UPDATED'].includes(currentDocument.status) && currentDocument.comment ?
            <VBox key={'comment' + index} gap={1} className={'pd-comment-item-card'}>
              <HBox gap={3}>
                <label className={'user you tp-text-semibold'}>{currentDocument.byUser}</label>
                <span
                  className={'tp-comment-date tp-color-light-LM'}>{this.translateByNamespace('date.dateWithTime', { date: new Date(currentDocument.timeStamp) })}</span>
              </HBox>
              <Container className={'msg tp-word-wrap'}>
                {currentDocument.comment}
              </Container>
            </VBox> : null)
        })}
      </>
    )
  };

  /**
   * Render proposed date as plain date or date picker
   *
   * @param supplierProposedDate
   * @param pmProposedDate
   * @param originalDueDate
   */
  renderProposedDueDate = (supplierProposedDate: number, pmProposedDate: number, originalDueDate: number) => {
    const { selectedTask } = this.state;
    const { userAction = '', status } = selectedTask;
    let displayDate = supplierProposedDate;
    let userText = 'supplier';
    if (pmProposedDate) {
      userText = 'projectManager';
      displayDate = pmProposedDate;
    }
    const dueDateCls = pmProposedDate ? 'pm-proposed-date' : '';
    const minDate = _.clone(displayDate);
    if (userAction === 'negotiate' && selectedTask.supplierProposedDueDate) {
      displayDate = selectedTask.supplierProposedDueDate;
    }

    if (['accept', 'decline'].includes(userAction) && !pmProposedDate) {
      displayDate = originalDueDate;
    }
    const datePickerEnabled = userAction === 'negotiate' || (!userAction && status === 'COUNTERED');
    return <>
      <HBox alignItemsCenter role={'columnheader'} gap={2}>
        <span>{this.translate('proposedDueDate')}</span>
        {this.translate(userText, {
          className: 'tp-color-accent tp-text-medium tp-font-size-sm'
        })}</HBox>
      <Container
        alignItemsCenter
        className={dueDateCls}
        displayFlex
        justifyContentEnd
        role={'gridcell'}
      >
        {pmProposedDate ? this.translateByNamespace('date.dateWithTime', {
            date: new Date(displayDate)
          }) :
          <LocalizedDatePicker
            ariaLabelLocale={this.translate('SRL-negotiationData')}
            disabled={!datePickerEnabled}
            format={this.translateByNamespace('date.datePickerFormat')}
            id={"negotiationData"}
            label={''}
            min={new Date(minDate) || undefined}
            name="negotiationData"
            onChange={(event) => this.onDateChange(event, minDate)}
            value={new Date(displayDate) || null}
          />}
      </Container>
    </>
  }

  /**
   * Right pane render
   */
  renderRightPane = () => {
    const { selectedTask, actionName = '' } = this.state;
    const originalDueDate = _.get(selectedTask, 'financialDetails[0].dueDate');
    const proposedDueDate = _.get(selectedTask, 'financialDetails[1].dueDate') || originalDueDate;
    const recounteredDueDate = _.get(selectedTask, 'financialDetails[2].dueDate');
    let radioData: { label: string, value: string, disabled?: boolean }[] = [
      {
        label: this.translate('accept'),
        value: 'accept'
      },
      {
        label: this.translate('decline'),
        value: 'decline'
      }
    ];
    if (selectedTask.allowNegotiation) {
      radioData.push({
        label: this.translate('negotiate'),
        disabled: selectedTask.status === 'UPDATED',
        value: 'negotiate'
      })
    }
    const referenceFilesCount = selectedTask.referenceFilesCount;
    const allParsable = _.get(selectedTask, 'parsableState') === 'ALL_PARSABLE';
    return <VBox
      className={'pd-offer-right-pane'}
      flex1
      gap={2}
      tabIndex={0}
      htmlTag={'section'}
    >
      <h3 className={'pd-offer-pane-title tp-text-semibold tp-font-size-lg'}>{this.translate('task')}</h3>
      <HBox alignItemsEnd justifyContentBetween>
        <VBox role={'grid'}>
          <HBox
            alignItemsStart
            className={'pd-offer-dialog-header-table'}
            gap={4}
            role={'rowgroup'}
          >
            <VBox role={'row'}>
              <Container role={'columnheader'}>{this.translate('task')}</Container>
              <Container role={'gridcell'}>{selectedTask.phaseName}</Container>
            </VBox>
            <VBox role={'row'}>
              <Container role={'columnheader'}>{this.translate('batch')}</Container>
              <Container role={'gridcell'}>{selectedTask.batchName}</Container>
            </VBox>
            <VBox role={'row'}>
              <Container role={'columnheader'}>{this.translate('rateType')}</Container>
              <Container role={'gridcell'}>
                <span
                  className={`pd-rate-type pd-${selectedTask.unitOfWork.toLowerCase()} tp-font-size-xs tp-text-medium`}>
                  {_.capitalize(selectedTask.unitOfWork)}
                </span>
              </Container>
            </VBox>
            <VBox role={'row'}>
              <Container role={'columnheader'}>{this.translate('currency')}</Container>
              <Container role={'gridcell'}>
                <span className={'pd-currency tp-font-size-xs tp-text-medium'}>
                  {this.props.responseData.currency}
                </span>
              </Container>
            </VBox>
          </HBox>
        </VBox>
        {!selectedTask.readOnly &&
          <RadioGroup data={radioData} layout={'horizontal'} value={actionName}
                      onChange={this.radioActionsOnChange} />


        }
      </HBox>
      <Container className={'tp-separator'} />
      <HBox className={'pd-overview-due-date-section'} gap={5}>
        <VBox flex1 gap={2}>
          <h3 className={'tp-text-semibold tp-font-size-md'}>{this.translate('overview')}</h3>
          <HBox
            className={'pd-overview-grid'}
            justifyContentBetween
            role={'grid'}>
            <Container role={'rowgroup'}>
              <VBox role={'row'}>
                <Container displayFlex alignItemsCenter role={'columnheader'}>{this.translate('wordCount')}</Container>
                <Container displayFlex alignItemsCenter role={'columnheader'}>{this.translate('files')}</Container>
                <Container displayFlex alignItemsCenter
                           role={'columnheader'}>{this.translate('referenceFiles')}</Container>
              </VBox>
            </Container>
            <Container role={'rowgroup'}>
              <VBox role={'row'}>
                <Container alignItemsCenter displayFlex justifyContentEnd role={'gridcell'}>
                  {selectedTask.batchLanguageWordCount !== null ? <>
                    {selectedTask.batchLanguageWordCount}
                    <PuzzleButton
                      titleText={this.translate('SRL-openWordCount')}
                      onClick={this.openWordCount}
                      aria-label={this.translate('SRL-openWordCount')}
                      iconClass={'tp-icon tp-open_in_new'}
                      fillMode={'flat'}
                      extraSmall
                      themeColor={'primary'}
                      tooltipPosition={'right'}
                      withTooltip
                    />
                  </> : '-'}
                </Container>
                <Container alignItemsCenter displayFlex justifyContentEnd role={'gridcell'}>
                  {selectedTask.batchLanguageFileCount}
                  {(allParsable && selectedTask.availableEditors) && !_.includes(selectedTask.availableEditors, 'SIDE_BY_SIDE') ?
                    <PuzzleButton
                      titleText={this.translate('SRL-openFiles')}
                      onClick={this.openFilesInPreviewMode}
                      aria-label={this.translate('SRL-openFiles')}
                      iconClass={'tp-icon tp-open_in_new'}
                      fillMode={'flat'}
                      extraSmall
                      themeColor={'primary'}
                      tooltipPosition={'right'}
                      withTooltip
                      disabled={!selectedTask.previewEnabled}
                    /> :
                    <PuzzleButton
                      titleText={this.translate('SRL-downloadFiles')}
                      onClick={this.downloadSourceFiles}
                      aria-label={this.translate('SRL-downloadFiles')}
                      iconClass={'tp-icon tp-download'}
                      fillMode={'flat'}
                      extraSmall
                      themeColor={'primary'}
                      tooltipPosition={'right'}
                      withTooltip
                      disabled={!selectedTask.previewEnabled}
                    />}
                </Container>
                <Container alignItemsCenter displayFlex justifyContentEnd role={'gridcell'}>
                  {referenceFilesCount ? (
                    <>
                      {referenceFilesCount}
                      <PuzzleButton
                        titleText={this.translate('SRL-downloadReferenceFiles')}
                        onClick={this.downloadReferenceFiles}
                        aria-label={this.translate('SRL-downloadReferenceFiles')}
                        iconClass={'tp-icon tp-download'}
                        fillMode={'flat'}
                        extraSmall
                        themeColor={'primary'}
                        tooltipPosition={'right'}
                        withTooltip
                      />
                    </>
                  ) : (
                    this.translate('-')
                  )}
                </Container>
              </VBox>
            </Container>
          </HBox>
        </VBox>
        <VBox flex1 gap={2}>
          <h3 className={'tp-text-semibold tp-font-size-md'}>{this.translate('dueDate')}</h3>
          <HBox
            className={'pd-due-date-grid'}
            justifyContentBetween
            role={'grid'}>
            <VBox className={'tp-100 tp-overflow-hidden'} role={'rowgroup'}>
              <HBox justifyContentBetween role={'row'}>
                {originalDueDate &&
                  <>
                    <HBox alignItemsCenter role={'columnheader'} gap={2}>
                      <span>{this.translate('dueDate')}</span>
                      {this.translate('projectManager', {
                        className: 'tp-color-accent tp-text-medium tp-font-size-sm'
                      })}</HBox>
                    <Container alignItemsCenter displayFlex justifyContentEnd role={'gridcell'}>
                      {this.translateByNamespace('date.dateWithTime', { date: new Date(originalDueDate) })}
                    </Container>
                  </>
                }
              </HBox>
              <HBox justifyContentBetween role={'row'}>
                {proposedDueDate && recounteredDueDate &&
                  <>
                    <HBox alignItemsCenter role={'columnheader'} gap={2}>
                      <span>
                        {this.translate('dueDate')}
                      </span>
                      {this.translate('supplier', {
                        className: 'tp-color-accent tp-text-medium tp-font-size-sm'
                      })}
                    </HBox>
                    <Container alignItemsCenter displayFlex justifyContentEnd role={'gridcell'}>
                      {this.translateByNamespace('date.dateWithTime', { date: new Date(proposedDueDate) })}
                    </Container>
                  </>
                }
              </HBox>
              <HBox alignItemsCenter flex1 className={'pd-proposed-due-date-row'} justifyContentBetween role={'row'}>
                {(proposedDueDate || recounteredDueDate) &&
                  <>
                    {this.renderProposedDueDate(proposedDueDate, recounteredDueDate, originalDueDate)}
                  </>
                }
              </HBox>
            </VBox>
          </HBox>
        </VBox>
      </HBox>
      <VBox gap={2} className={'pd-offer-grid-section'}>
        <h3 className={'tp-text-semibold tp-font-size-md'}>{this.translate('offer')}</h3>
        <NegotiationGrid selectedTask={_.cloneDeep(selectedTask)} actionName={actionName}
                         budgetRatesUpdated={this.updateBudgetRates}
                         roundingDigits={this.props.responseData.roundingDigits} />
      </VBox>
      <VBox gap={2} className={'pd-comment-section'}>
        {this.renderCommentSection()}
      </VBox>
    </VBox>
  };

  /**
   * Task grid render
   */
  taskListRender = () => {
    const { data = [] } = this.state;
    const footerTotalData = data.reduce((acc, item) => {
      if ((item.status !== 'PROVIDED' && !item.userAction) || (item.userAction && item.userAction !== 'decline')) {
        acc = acc.plus(item.offer);
      }
      return acc;
    }, new Big(0));
    const roundingDigits: number = this.props.responseData.roundingDigits;
    return <KendoGridAbstract
      captionText={this.translate("SRL-taskListCaptionText")}
      cellRender={this.cellRender}
      className={'tp-no-border'}
      data={data}
      onRowClick={this.taskListOnSelectionChange}
      rowHeight={32}
      rowRender={this.taskListRowRender}
      selectedField={'selected'}
    >
      <Column
        field={'phaseName'}
        footerCell={() => <td>{this.translate('total')}</td>}
        title={this.translate('task')}
      />
      <Column
        field={'batchName'}
        title={this.translate('batch')}
      />
      <Column
        field='dueDate'
        title={this.translate('dueDate')}
      />
      <Column
        field={'offer'}
        headerClassName={'k-text-right pd-offer-header-cell'}
        footerCell={() => <td>
          {footerTotalData.valueOf() !== '0'
            ? `${this.props.responseData.currency} ${footerTotalData.toFixed(roundingDigits)}`
            : '-'}
        </td>
        }
        title={this.translate('offer')}
      />
    </KendoGridAbstract>
  };

  /**
   * On row click to set id of selected row
   *
   * @param event
   */
  taskListOnSelectionChange = (event: GridRowClickEvent) => {
    const dataItem = event.dataItem;
    let statusAction = '';
    switch (dataItem.status) {
      case 'ACCEPTED':
      case 'APPROVED':
        statusAction = 'accept';
        break;
      case 'COUNTERED':
        statusAction = 'negotiate'
        break;
      default:
        break;
    }
    this.setState({ selectedTask: dataItem, actionName: dataItem.userAction || statusAction }, () => {
      const { selectedTask } = this.state;
      if (!this.allReadOnlyTasks) {
        this.dispatch('updateFooterButtons', '', {
          displayCVABadge: selectedTask.cva,
          enableNext: this.changesExists
        });
      }
    });
  }

  /**
   * Task list row render. Using to highlight selected row
   *
   * @param trElement
   * @param props
   */
  taskListRowRender = (trElement: ReactElement<HTMLTableRowElement>, props: GridRowProps): ReactElement<HTMLTableRowElement> => {
    const { selectedTask } = this.state;
    const isSelected = props.dataItem.scopeId === selectedTask.scopeId;
    const elementProps = { ...trElement.props };

    if (isSelected) {
      elementProps.className += ' k-selected';
    }

    const trProps = {
      ...elementProps
    };

    return React.cloneElement(trElement, { ...trProps }, elementProps.children);
  };

  /**
   * Fires on negotiation grid change. Updates budget rates
   *
   * @param rates
   * @param total
   */
  updateBudgetRates = ({ rates, total }: { rates: POST_BUDGET_RATE[], total: number }) => {
    let { selectedTask, data } = this.state;
    selectedTask.budgetRates = rates;
    data = data.map((currentTask: OFFER_TASK) => {
      if (currentTask.scopeId === selectedTask.scopeId) {
        currentTask.offer = total;
        currentTask.budgetRates = rates;
      }
      return currentTask;
    });
    this.setState({ selectedTask, data });
  };

  renderBody() {
    const { responseData } = this.props;
    const { backgroundEnabled, background, instructionsEnabled, instructions } = this.state.selectedTask;
    const hasBackgroundOrInstructions = (backgroundEnabled && background) || (instructionsEnabled && instructions);
    const declineAvailable = this.state.data.some((currentData: OFFER_TASK) => ["PROVIDED", "COUNTERED", "UPDATED", "ACCEPTED"].includes(currentData.status));
    return (
      <HBox className={'pd-offer-dialog-content'} flex1>
        <VBox className={'pd-offer-left-pane'} gap={2} tabIndex={0} htmlTag={'section'}>
          <h3 className={'pd-offer-pane-title tp-text-semibold tp-font-size-lg'}>{this.translate('submission')}</h3>
          <VBox role={'grid'}>
            <HBox
              alignItemsStart
              className={'pd-offer-dialog-header-table'}
              gap={6}
              role={'rowgroup'}
            >
              <VBox role={'row'}>
                <Container role={'columnheader'}>{this.translate('claimLevel')}</Container>
                <Container role={'gridcell'}>{_.startCase(_.lowerCase(responseData.claimLevel))}</Container>
              </VBox>
              <VBox role={'row'}>
                <Container role={'columnheader'}>{this.translate('target')}</Container>
                <Container
                  role={'gridcell'}>{this.state.selectedTask.targetLanguage?.targetLanguageDisplayName}</Container>
              </VBox>
              <VBox role={'row'}>
                <Container role={'columnheader'}>{this.translate('instructionNotes')}</Container>
                <Container role={'gridcell'}>
                  {hasBackgroundOrInstructions ? (
                    <>
                      {this.translate('Yes')}
                      <PuzzleButton
                        titleText={this.translate('SRL-openBackgroundInstructions')}
                        onClick={this.openBackgroundInstructions}
                        aria-label={this.translate('SRL-openBackgroundInstructions')}
                        iconClass="tp-icon tp-open_in_new"
                        fillMode="flat"
                        size="small"
                        themeColor="primary"
                        tooltipPosition="right"
                        withTooltip
                      />
                    </>
                  ) : (
                    this.translate('emptyNotes')
                  )}
                </Container>
              </VBox>
            </HBox>
          </VBox>
          <VBox className={'pd-available-task-section'} gap={2}>
            <HBox justifyContentBetween>
              <h3
                className={'tp-text-semibold tp-font-size-md'}>{this.translate('availableTasks', { count: this.state.data.length })}</h3>
              <PuzzleButton
                disabled={!declineAvailable}
                fillMode={'outline'}
                onClick={this.declineAllTask}
                size={'small'}
              >
                {this.translate('declineAllAvailableTasks')}
              </PuzzleButton>
            </HBox>
            {this.taskListRender()}
          </VBox>
        </VBox>
        {this.renderRightPane()}
        <TpTooltip
          ref={(el: any) => this.tooltip = el}
          anchorElement="target"
          openDelay={1000}
          position={'bottom'}
        />
      </HBox>
    );
  }
}

export default OfferDialog;
